import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  ElementRef,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { GridApi, GridOptions, ColumnApi } from 'ag-grid-community';
import { DatePipe } from '@angular/common';
import { FinanceExpenseService } from './finance-expense.service';
import { Store } from '@ngrx/store';
import { getCurrentUser } from 'app/core/store/currentUser/user.selectors';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DateUtility } from '../common/utilities/date.utility';
import { ConsultantModel } from '../common/models/consultant.model';
import { SnackbarService } from '../common/services/snackbar.service';
import { ConsultantDetailsService } from '../common/services/consultant-details.service';
import { CurrencyModel } from '../common/models/currency.model';
import { EntryDialogComponent } from '../actuals/entry/entry-dialog/entry-dialog.component';
import { Constants } from '../common/utilities/constants.config';
import { ExpenseEntryModel } from '../common/models/ExpenseEntry.model';
import { finalize } from 'rxjs';
import { MatSelect } from '@angular/material/select';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-finance-expense',
  templateUrl: './finance-expense.component.html',
  styleUrls: ['./finance-expense.component.scss'],
})
export class FinanceExpenseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  gridOptions: GridOptions = {};
  agGridAPI: GridApi;
  agGridColumnAPI: ColumnApi;
  defaultColDef: any;
  rowData: any = [];
  currentUser: ConsultantModel;
  currencies: any[] = [];
  selectedCurrency: CurrencyModel[] = [];
  countries: any[] = [];
  selectedCountry: any[] = [];
  consultantList: ConsultantModel[] = [];
  selectedConsultant: ConsultantModel[] = [];
  isLoading: boolean = false;
  modifiedEntries: ExpenseEntryModel[] = [];
  hasModifiedEntries: boolean = false;
  expenseFinanceEntryIsUnsavedChangesPresent: boolean;

  // Reference to the select element for consultants
  @ViewChild('consultantSelect') consultantSelect: MatSelect;

  // Filter state - now used directly in the placeholder
  consultantFilter: string = '';
  isConsultantSelectOpen: boolean = false;
  selectSubscriptions: Subscription[] = [];

  // For tracking manual changes to preserve selections
  private previousSelections: ConsultantModel[] = [];

  // Track which consultants are being shown based on the filter
  private visibleConsultantIds: Set<string> = new Set();

  // List of consultants filtered by search text
  filteredConsultantList: ConsultantModel[] = [];

  dateRange = this.formBuilder.group({
    start: [null as Date | null],
    end: [null as Date | null],
  });

  constructor(
    private datePipe: DatePipe,
    private financeExpenseService: FinanceExpenseService,
    private store: Store,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private consultantDetailsService: ConsultantDetailsService,
    private snackbarService: SnackbarService
  ) {
    this.store.select(getCurrentUser).subscribe((data) => {
      this.currentUser = data;
    });
    this.financeExpenseService.setExpenseFinanceEntryIsUnsavedChangesPresent(
      false
    );
    this.financeExpenseService.expenseFinanceEntryIsUnsavedChangesPresent.subscribe(
      (data) => {
        this.expenseFinanceEntryIsUnsavedChangesPresent = data;
      }
    );
  }

  ngOnInit() {
    this.setupGridOptions();
    this.loadConsultants();
    this.loadCountries();
    this.initializeDateRange();
    this.loadCurrencies();
    this.financeExpenseService.setExpenseFinanceEntryIsUnsavedChangesPresent(
      false
    );
    this.financeExpenseService.expenseFinanceEntryIsUnsavedChangesPresent.subscribe(
      (data) => {
        this.expenseFinanceEntryIsUnsavedChangesPresent = data;
      }
    );

    // Initialize filtered consultant list
    this.updateFilteredConsultantList();
  }

  ngAfterViewInit() {
    // Set up the keyboard event listening for the consultant select
    this.setupConsultantSelectSearchable();
  }

  // Clean up subscriptions when component is destroyed
  ngOnDestroy() {
    this.selectSubscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Sets up the consultant select to be searchable
   */
  setupConsultantSelectSearchable() {
    if (!this.consultantSelect) return;

    // Listen for open/close events
    const openSub = this.consultantSelect.openedChange.subscribe((isOpen) => {
      this.isConsultantSelectOpen = isOpen;

      if (isOpen) {
        // When opening, store the current selection state
        this.previousSelections = [...this.selectedConsultant];

        // Initialize the filtered list
        this.updateFilteredConsultantList();
      } else {
        // Clear filter when dropdown closes
        setTimeout(() => {
          this.consultantFilter = '';
          this.updateFilteredConsultantList();
        }, 100); // Small delay to avoid flicker
      }
    });

    const selectSub = this.consultantSelect.selectionChange.subscribe(
      (event) => {
        if (this.consultantFilter && this.consultantFilter.length > 0) {
          // We're in a filtered state

          // Get current selections after Mat-Select has updated its model
          setTimeout(() => {
            // Get all consultants selected through the UI (will only include filtered ones)
            const currentSelections = [...this.selectedConsultant];

            // Get previously selected consultants that are not currently visible
            const nonVisibleSelections = this.previousSelections.filter(
              (c) => !this.visibleConsultantIds.has(c.Employee)
            );

            // Combine them - keeping ONLY visible selections from current selection
            // and all non-visible from previous
            const visibleSelections = currentSelections.filter((c) =>
              this.visibleConsultantIds.has(c.Employee)
            );

            // Update the model with the combined list
            this.selectedConsultant = [
              ...nonVisibleSelections,
              ...visibleSelections,
            ];

            // Update our tracking of previous selections
            this.previousSelections = [...this.selectedConsultant];

            // ENHANCEMENT: Clear the filter text after selection
            this.consultantFilter = '';

            // Update the filtered list to show all consultants again
            this.updateFilteredConsultantList();

            // Force Angular change detection to update the UI
            this.consultantSelect.stateChanges.next();
          });
        }
      }
    );

    this.selectSubscriptions.push(openSub, selectSub);
  }

  /**
   * Listen for keydown events globally when the consultant select is open
   */
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Only process keyboard events when the consultant select is open
    if (this.isConsultantSelectOpen) {
      // Store current selection state before filter changes
      this.previousSelections = [...this.selectedConsultant];

      // Ignore special keys (arrows, enter, tab, etc.)
      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.metaKey
      ) {
        this.consultantFilter += event.key;

        // Update filtered list
        this.updateFilteredConsultantList();

        // Force Angular change detection
        this.consultantSelect.stateChanges.next();

        // Prevent default to avoid interfering with select behavior
        event.preventDefault();
      } else if (event.key === 'Backspace') {
        this.consultantFilter = this.consultantFilter.slice(0, -1);

        // Update filtered list
        this.updateFilteredConsultantList();

        this.consultantSelect.stateChanges.next();
        event.preventDefault();
      } else if (event.key === 'Escape') {
        this.consultantFilter = '';

        // Update filtered list
        this.updateFilteredConsultantList();

        this.consultantSelect.stateChanges.next();
        // Let Escape bubble up to close the select if needed
      }
    }
  }

  /**
   * Updates the filtered consultant list based on the search filter
   */
  updateFilteredConsultantList() {
    if (!this.consultantList) {
      this.filteredConsultantList = [];
      return;
    }

    // If no filter, show all consultants
    if (!this.consultantFilter) {
      this.filteredConsultantList = [...this.consultantList];
    } else {
      // Apply filter
      const filter = this.consultantFilter.toLowerCase();
      this.filteredConsultantList = this.consultantList.filter(
        (consultant) =>
          consultant.FirstName.toLowerCase().includes(filter) ||
          consultant.LastName.toLowerCase().includes(filter) ||
          consultant.Employee.toLowerCase().includes(filter)
      );
    }

    // Sort to put selected consultants at the top
    this.filteredConsultantList.sort((a, b) => {
      const aSelected = this.selectedConsultant.some(
        (c) => c.Employee === a.Employee
      );
      const bSelected = this.selectedConsultant.some(
        (c) => c.Employee === b.Employee
      );

      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // If both or neither are selected, sort by name
      return `${a.FirstName} ${a.LastName}`.localeCompare(
        `${b.FirstName} ${b.LastName}`
      );
    });

    // Update our tracking set of visible consultants
    this.updateVisibleConsultants();
  }

  /**
   * Updates the internal set of visible consultant IDs based on current filter
   */
  private updateVisibleConsultants() {
    this.visibleConsultantIds.clear();

    if (!this.filteredConsultantList) return;

    // Update our tracking set with the filtered list
    this.filteredConsultantList.forEach((c) => {
      this.visibleConsultantIds.add(c.Employee);
    });
  }

  loadCountries() {
    this.financeExpenseService.getCountries().subscribe(
      (countries) => {
        this.countries = countries;
      },
      (error) => {
        this.snackbarService.showSnackBar(
          'Error loading countries',
          'close',
          'failure'
        );
      }
    );
  }

  loadCurrencies() {
    this.financeExpenseService.getCurrencies().subscribe(
      (currencies) => {
        this.currencies = currencies;
      },
      (error) => {
        this.snackbarService.showSnackBar(
          'Error loading currencies',
          'close',
          'failure'
        );
      }
    );
  }

  loadConsultants() {
    this.consultantDetailsService.getConsultantDetails().subscribe(
      (consultants) => {
        this.consultantList = consultants;
        // Initialize filtered list after loading consultants
        this.updateFilteredConsultantList();
      },
      (error) => {
        this.snackbarService.showSnackBar(
          'Error loading consultants',
          'close',
          'failure'
        );
      }
    );
  }

  private setupGridOptions() {
    this.defaultColDef = {
      width: 100,
      filter: true,
      resizable: true,
      sortable: true,
    };

    this.gridOptions = {
      columnDefs: [
        {
          headerName: 'Employee',
          field: 'Employee',
          width: 250,
        },
        {
          headerName: 'Client',
          field: 'Client',
          valueGetter: (params) => {
            if (!params.data) return '';
            return `${params.data.Client || ''} ${
              params.data.ClientName ? '- ' + params.data.ClientName : ''
            }`.trim();
          },
          width: 250,
        },
        {
          headerName: 'Project',
          field: 'Project',
          valueGetter: (params) => {
            if (!params.data) return '';
            return `${params.data.Project || ''} ${
              params.data.Project_Description
                ? '- ' + params.data.Project_Description
                : ''
            }`.trim();
          },
          width: 300,
        },
        {
          headerName: 'Expense Type',
          field: 'ExpenseType',
          width: 270,
        },
        {
          headerName: 'Description',
          field: 'Description',
          width: 350,
          editable: true,
        },
        {
          headerName: 'Details',
          field: 'Details',
          width: 300,
          editable: true,
        },
        {
          headerName: 'Amount',
          field: 'AmountSpent',
          type: 'numericColumn',
          cellClass: 'excelNumber',
          editable: true,
          valueFormatter: (params) => {
            if (!params.data) return '';
            const value = params.value ? Number(params.value) : 0;
            return `${params.data.Currency_symbol_short || ''} ${value.toFixed(
              2
            )}`;
          },
          width: 200,
        },
        {
          headerName: 'Tax Amount',
          field: 'Tax_Amount',
          cellClass: 'excelNumber',
          enablePivot: true,
          enableRowGroup: true,
          enableValue: true,
          valueGetter: (params) => {
            if (!params.node.group) {
              return params.data.Tax_Amount === null
                ? 0
                : parseFloat(params.data.Tax_Amount);
            }
            return null;
          },
          valueFormatter: (params) => {
            if (!params.node.group) {
              const value = params.value === null ? 0 : params.value;
              return (
                params.node.data.Currency_symbol_short + ' ' + value.toFixed(2)
              );
            }
            return null;
          },
          width: 180,
        },
        {
          headerName: 'Cur',
          field: 'Currency_From',
          width: 130,
        },
        {
          headerName: 'Payment Method',
          field: 'PaymentMethod',
          width: 190,
        },
        {
          headerName: 'Date',
          field: 'TDate',
          valueFormatter: (params) => {
            if (!params.value) return '';
            return this.datePipe.transform(params.value, 'dd MMM yyyy');
          },
          width: 220,
          sort: 'asc',
        },
        {
          headerName: 'Invoice Date',
          field: 'Invoice_date',
          valueFormatter: (params) => {
            if (!params.value) return '';
            return this.datePipe.transform(params.value, 'dd MMM yyyy');
          },
          width: 220,
        },
      ],
      excelStyles: [
        {
          id: 'excelNumber',
          numberFormat: { format: '#,##0.00' },
        },
      ],
      getRowId: (params) => params.data.ExpenseID,
      animateRows: true,
      suppressDragLeaveHidesColumns: true,
      rowSelection: 'multiple',
      suppressRowClickSelection: true,
      getRowStyle: (params) => {
        let rowStyle = { background: 'white' };

        if (params.data) {
          if (params.data.status === 'update') {
            rowStyle = { background: '#ffc107' };
          } else if (
            params.data.originalValues &&
            Object.keys(params.data.originalValues).length > 0
          ) {
            rowStyle = { background: '#ffc107' };
          }
        }

        return rowStyle;
      },
      onCellValueChanged: (params) => {
        this.handleCellValueChange(params);
      },
      onRowDoubleClicked: (params) => this.onRowDoubleClicked(params),
    };
  }

  onRowDoubleClicked(params: any): void {
    if (!params.node.group) {
      const dialogConfig = {
        maxWidth: 'clamp(340px, 92vw, 900px)',
        width: '100%',
        height: 'auto',
        disableClose: true,
        data: {
          ...params.data,
          entryPageType: Constants.NAV_EXPENSE_ENTRY,
          Mode: 'edit',
          startDate: DateUtility.formatDatefromDate(this.dateRange.value.start),
          endDate: DateUtility.formatDatefromDate(this.dateRange.value.end),
        },
      };

      const dialogRef = this.dialog.open(EntryDialogComponent, dialogConfig);

      dialogRef.afterClosed().subscribe((result) => {
        if (result && typeof result !== 'boolean') {
          const rowIndex = this.rowData.findIndex(
            (row) => row.ExpenseID === params.data.ExpenseID
          );

          if (rowIndex !== -1) {
            this.rowData[rowIndex] = {
              ...this.rowData[rowIndex],
              ...result,
              status: 'update',
            };

            this.modifiedEntries = this.modifiedEntries.filter(
              (entry) => entry.ExpenseID !== this.rowData[rowIndex].ExpenseID
            );
            this.modifiedEntries.push(this.rowData[rowIndex]);
            this.hasModifiedEntries = true;
            this.financeExpenseService.setExpenseFinanceEntryIsUnsavedChangesPresent(
              true
            );

            if (this.agGridAPI) {
              this.agGridAPI.setRowData(this.rowData);
            }
          }
        }
      });
    }
  }

  onGridReady(params: any) {
    this.agGridAPI = params.api;
    this.agGridColumnAPI = params.columnApi;

    if (this.agGridAPI) {
      this.agGridAPI.sizeColumnsToFit();
    }
  }

  initializeDateRange() {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    this.dateRange.patchValue({
      start: firstDay,
      end: lastDay,
    });
  }

  clearFilters() {
    this.selectedCurrency = [];
    this.selectedConsultant = [];
    this.previousSelections = [];
    this.selectedCountry = [];
    this.consultantFilter = '';
    this.initializeDateRange();
    this.rowData = [];

    // Update filtered consultant list
    this.updateFilteredConsultantList();

    if (this.agGridAPI) {
      this.agGridAPI.setRowData([]);
    }

    this.snackbarService.showSnackBar('Filters cleared', 'close', 'success');
  }

  applyFilters() {
    if (!this.dateRange.value.start || !this.dateRange.value.end) {
      this.snackbarService.showSnackBar(
        'Please select a date range',
        'close',
        'failure'
      );
      return;
    }

    this.isLoading = true;
    const startDate = new Date(this.dateRange.value.start);
    const endDate = new Date(this.dateRange.value.end);
    const filters = {
      startDate: DateUtility.formatDatefromDate(startDate),
      endDate: DateUtility.formatDatefromDate(endDate),
      ...(this.selectedConsultant?.length && {
        selectedConsultants: this.selectedConsultant,
      }),
      ...(this.selectedCurrency?.length && {
        selectedCurrency: this.selectedCurrency,
      }),
      ...(this.selectedCountry?.length && {
        selectedCountry: this.selectedCountry,
      }),
    };

    this.financeExpenseService.getExpenseData(filters).subscribe({
      next: (data) => {
        const cleanedData = data.map((entry) => ({
          ...entry,
          status: 'clean',
        }));

        this.rowData = [...cleanedData];

        if (this.agGridAPI) {
          this.agGridAPI.setRowData(this.rowData);
          setTimeout(() => {
            this.agGridAPI.sizeColumnsToFit();
          });
        }

        this.modifiedEntries = [];
        this.hasModifiedEntries = false;
        this.financeExpenseService.setExpenseFinanceEntryIsUnsavedChangesPresent(
          false
        );

        this.isLoading = false;
      },
      error: (error) => {
        this.snackbarService.showSnackBar(
          'Error loading expense data',
          'close',
          'failure'
        );
        this.isLoading = false;
      },
    });
  }

  exportToExcel() {
    if (!this.agGridAPI || !this.rowData?.length) {
      this.snackbarService.showSnackBar('No data to export', 'close', 'info');
      return;
    }

    try {
      const startDate = DateUtility.formatDatefromDate(
        new Date(this.dateRange.value.start)
      );
      const endDate = DateUtility.formatDatefromDate(
        new Date(this.dateRange.value.end)
      );

      this.agGridAPI.exportDataAsExcel({
        fileName: `Finance_Expense_${startDate}_to_${endDate}.xlsx`,
        sheetName: 'Finance Expense',
        // A global processCellCallback bypasses the per-column valueFormatter on
        // export, so we shape the values here:
        //  - Amount / Tax Amount -> raw numbers (summable in Excel). The per-row
        //    currency is preserved in the "Cur" column (Currency_From).
        //  - Date columns -> formatted so they stay readable.
        processCellCallback: (params) => {
          const field = params.column.getColDef().field;
          if (field === 'AmountSpent' || field === 'Tax_Amount') {
            const num = Number(params.value);
            return isNaN(num) ? 0 : num;
          }
          if (field === 'TDate' || field === 'Invoice_date') {
            return params.value
              ? this.datePipe.transform(params.value, 'dd MMM yyyy')
              : '';
          }
          return params.value;
        },
      });

      this.snackbarService.showSnackBar('Exported to Excel', 'close', 'success');
    } catch (error) {
      console.error('Excel export failed', error);
      this.snackbarService.showSnackBar(
        'Excel export failed',
        'close',
        'failure'
      );
    }
  }

  handleCellValueChange(event: any) {
    if (!event.data) return;

    const rowIndex = this.rowData.findIndex(
      (row) => row.ExpenseID === event.data.ExpenseID
    );
    if (rowIndex !== -1) {
      this.rowData[rowIndex] = {
        ...this.rowData[rowIndex],
        status: 'update',
      };

      this.modifiedEntries = this.modifiedEntries.filter(
        (entry) => entry.ExpenseID !== event.data.ExpenseID
      );
      this.modifiedEntries.push(this.rowData[rowIndex]);

      this.hasModifiedEntries = this.modifiedEntries.length > 0;
      this.financeExpenseService.setExpenseFinanceEntryIsUnsavedChangesPresent(
        this.hasModifiedEntries
      );

      if (this.agGridAPI) {
        this.agGridAPI.refreshCells({ force: true });
      }
    }
  }

  onSelectionChanged() {
    const selectedRows = this.agGridAPI.getSelectedRows();
  }

  saveModifiedEntries() {
    if (this.modifiedEntries.length === 0) {
      this.snackbarService.showSnackBar('No entries to save', 'close', 'info');
      return;
    }

    this.isLoading = true;

    const savePayload = this.modifiedEntries.map((entry) => {
      const entryToSave = { ...entry };
      entryToSave.ExpenseID = entryToSave.ExpenseID || 0;
      return entryToSave;
    });

    this.financeExpenseService
      .saveExpenseEntries(savePayload)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (response) => {
          // Backend returns result: 0 on success (0 = no errors), non-zero on failure.
          if (response && response.result === 0) {
            this.snackbarService.showSnackBar(
              response.Message || 'Expense entries saved successfully',
              'close',
              'success'
            );

            this.modifiedEntries = [];
            this.hasModifiedEntries = false;
            this.financeExpenseService.setExpenseFinanceEntryIsUnsavedChangesPresent(
              false
            );

            this.applyFilters();
          } else {
            this.snackbarService.showSnackBar(
              response?.Message || 'Error saving expense entries',
              'close',
              'failure'
            );
          }
        },
        error: (error) => {
          this.snackbarService.showSnackBar(
            'Error saving expense entries',
            'close',
            'failure'
          );
        },
      });
  }
}
